import React, { Component, ReactNode } from 'react';
import { recoveryService } from '../services/recoveryService';
import ErrorRecoveryModal from './ErrorRecoveryModal';
import { handleRuntimeChunkError, isChunkLoadError } from '../utils/runtimeStability';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: any;
  errorInfo: any;
  showRecoveryModal: boolean;
  recoveryActions: any[];
}

class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showRecoveryModal: false,
      recoveryActions: []
    };
  }

  static getDerivedStateFromError(error: any): Partial<State> {
    return { hasError: true, error };
  }

  async componentDidCatch(error: any, errorInfo: any) {
    if (isChunkLoadError(error)) {
      await handleRuntimeChunkError(error, 'global-error-boundary');
      return;
    }

    // React Error #306 özel işlemi
    if (error?.message?.includes('306') || error?.stack?.includes('yue')) {
      console.warn('⚠️ React Error #306 yakalandı, sayfa yenileniyor...');
      setTimeout(() => window.location.reload(), 1000);
      return;
    }
    
    console.error('🔴 GLOBAL HATA DETAYI:', {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      location: window.location.pathname,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString()
    });
    
    this.setState({ errorInfo });

    // Attempt auto recovery
    const recovered = await recoveryService.attemptAutoRecovery(error);
    
    if (!recovered) {
      // Show recovery modal
      const actions = recoveryService.getUserRecoveryActions(error);
      this.setState({
        showRecoveryModal: true,
        recoveryActions: actions
      });
    }

    // Notify super admin
    await recoveryService.notifySuperAdmin(error, {
      componentStack: errorInfo.componentStack,
      location: window.location.pathname,
      timestamp: Date.now()
    });
  }

  handleRecoveryModalClose = () => {
    this.setState({ showRecoveryModal: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h1 className="text-lg font-semibold text-gray-900">Bir Hata Oluştu</h1>
                  <p className="text-sm text-gray-600">Uygulama beklenmeyen bir hatayla karşılaştı</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded p-3 mb-4">
                <p className="text-sm text-gray-700">
                  {this.state.error?.message || 'Bilinmeyen hata'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Sayfayı Yenile
                </button>
                <button
                  onClick={() => window.location.href = '/anasayfa'}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                >
                  Ana Sayfaya Dön
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                Sorun devam ederse sistem yöneticisine bildirilmiştir.
              </p>
            </div>
          </div>

          <ErrorRecoveryModal
            isOpen={this.state.showRecoveryModal}
            onClose={this.handleRecoveryModalClose}
            error={this.state.error}
            recoveryActions={this.state.recoveryActions}
          />
        </>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;

